/**
 * Offline Storage Manager
 * Handles storing data when offline and syncing when online
 */

class OfflineStorageManager {
  constructor() {
    this.dbName = 'SchoolAppDB';
    this.storeName = 'pendingRequests';
    this.db = null;
    this.init();
  }

  /**
   * Initialize IndexedDB
   */
  init() {
    if ('indexedDB' in window) {
      const request = indexedDB.open(this.dbName, 1);

      request.onerror = () => {
        console.error('Failed to open IndexedDB');
      };

      request.onsuccess = (event) => {
        this.db = event.target.result;
        console.log('IndexedDB initialized');
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          db.createObjectStore(this.storeName, { keyPath: 'id', autoIncrement: true });
        }
      };
    } else {
      console.warn('IndexedDB not supported, using fallback storage');
    }
  }

  /**
   * Save a request for later sync
   */
  async savePendingRequest(request) {
    if (!this.db) {
      return this.saveToLocalStorage(request);
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const addRequest = store.add({
        ...request,
        timestamp: new Date().toISOString(),
      });

      addRequest.onsuccess = () => {
        console.log('Request saved for offline sync');
        resolve(addRequest.result);
      };

      addRequest.onerror = () => {
        console.error('Failed to save request');
        reject(addRequest.error);
      };
    });
  }

  /**
   * Get all pending requests
   */
  async getPendingRequests() {
    if (!this.db) {
      return this.getFromLocalStorage();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const getAllRequest = store.getAll();

      getAllRequest.onsuccess = () => {
        resolve(getAllRequest.result);
      };

      getAllRequest.onerror = () => {
        reject(getAllRequest.error);
      };
    });
  }

  /**
   * Delete a pending request after successful sync
   */
  async deletePendingRequest(id) {
    if (!this.db) {
      return this.deleteFromLocalStorage(id);
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const deleteRequest = store.delete(id);

      deleteRequest.onsuccess = () => {
        console.log('Pending request deleted');
        resolve();
      };

      deleteRequest.onerror = () => {
        reject(deleteRequest.error);
      };
    });
  }

  /**
   * Fallback: Save to localStorage
   */
  saveToLocalStorage(request) {
    const requests = JSON.parse(localStorage.getItem('pendingRequests') || '[]');
    requests.push({
      ...request,
      id: Date.now(),
      timestamp: new Date().toISOString(),
    });
    localStorage.setItem('pendingRequests', JSON.stringify(requests));
  }

  /**
   * Fallback: Get from localStorage
   */
  getFromLocalStorage() {
    return JSON.parse(localStorage.getItem('pendingRequests') || '[]');
  }

  /**
   * Fallback: Delete from localStorage
   */
  deleteFromLocalStorage(id) {
    const requests = JSON.parse(localStorage.getItem('pendingRequests') || '[]');
    const filtered = requests.filter((req) => req.id !== id);
    localStorage.setItem('pendingRequests', JSON.stringify(filtered));
  }

  /**
   * Clear all pending requests
   */
  async clearAll() {
    if (this.db) {
      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([this.storeName], 'readwrite');
        const store = transaction.objectStore(this.storeName);
        const clearRequest = store.clear();

        clearRequest.onsuccess = () => resolve();
        clearRequest.onerror = () => reject(clearRequest.error);
      });
    } else {
      localStorage.removeItem('pendingRequests');
    }
  }

  /**
   * Get offline status
   */
  isOnline() {
    return navigator.onLine;
  }

  /**
   * Watch for online/offline changes
   */
  onStatusChange(callback) {
    window.addEventListener('online', () => callback(true));
    window.addEventListener('offline', () => callback(false));
  }
}

// Export singleton instance
export default new OfflineStorageManager();
